import { useQuery } from '@tanstack/react-query';
import type { AssignmentShiftList } from '../../../../../../js/api';
import { AssignmentsService } from '../../../../../../js/api';

const SHIFTS_QUERY_KEY = 'provider-assignment-shifts';

type Window = { start: string; end: string };

/**
 * Fetch the current provider's AssignmentShifts within a date window for the
 * Provider Home schedule calendar.
 *
 * Drives booked-day painting and the day-count badges directly off shift
 * records (one cell per shift's start_on..end_on range) instead of synthesizing
 * days from assignment.start_on..end_on, which is wrong for any assignment
 * whose shifts don't cover every day in that range.
 *
 * Provider-scoping comes from the AssignmentShiftViewSet's SecurityContextMixin
 * (provider_security_field = "assignment__provider_id"). No status filter is
 * passed: the server excludes terminal-negative shift statuses (DECLINED,
 * CANCELED, WITHDRAWN) and soft-deleted rows already, and the calendar's
 * bucket mapper handles bucketing the remaining statuses client-side.
 */
export const useProviderAssignmentShifts = ({ start, end }: Window) => {
  return useQuery({
    queryKey: [SHIFTS_QUERY_KEY, 'me', { start, end }],
    queryFn: async (): Promise<AssignmentShiftList[]> =>
      AssignmentsService.assignmentsAssignmentShiftList({
        startOnAfter: start,
        startOnBefore: end,
      }),
    staleTime: 5 * 60 * 1000,
  });
};
